import { CompRef, PageRef, RouterRef } from '@wix/document-services-types';
import { AppInstallOption } from '@wix/editor-platform-host-integration-apis';

import {
  AppData,
  BuilderComponent,
  Container,
  RouterWithRequiredData,
  UnifiedComponentAddedToTheStageData,
  UnifiedComponentsCollection,
  UnifiedWidget,
} from '../../../../types/unifiedComponents';
import { ExtendedPlatformContext } from '../../../../types/platformApi';
import { HOME_PAGE_ID } from '../../intentInstallService';
import {
  getAddUnifiedComponentsMethods,
  TOKEN,
} from './addComponents/addUnifiedComponents';
import { chainPromisesAsync, getLayouts } from './utils';
import { ensureValidRouterPrefix } from './appRouter/ensureValidRouterPrefix';
import { BIReporter } from './bi';
import {
  createUnifiedComponentsInstallationError,
  errorTypeMapping,
  UnifiedComponentsInstallationErrorCode as ErrorType,
} from './errors';

import { EditorPointer } from '@wix/editor-document-logic-api';

export function installUnifiedComponents(
  context: ExtendedPlatformContext,
  biReporter: BIReporter,
  appData: AppData,
  collection: UnifiedComponentsCollection,
  appOptions?: AppInstallOption,
) {
  const addedToStage: UnifiedComponentAddedToTheStageData = {
    pages: {},
    widgets: {},
  };

  if (appOptions?.headlessInstallation) {
    return Promise.resolve(addedToStage);
  }

  const homePage = context.documentServices.pages.getReference(
    context.documentServices.homePage.get(),
  ) as PageRef;
  homePage.pageId = homePage.id;

  const { builderComponents, pages, routers, widgets } = collection;
  const { addComponent, addPage, addRouter, addWidget } =
    getAddUnifiedComponentsMethods(context, appData);

  function installUnifiedPages() {
    const addSinglePage = ([guid, container]: [string, Container]) =>
      biReporter.withBI(
        'NEW_COMPONENTS_INSTALL',
        {
          componentId: guid,
          componentType: 'page',
          appDefinitionId: appData.appDefinitionId,
        },
        () =>
          new Promise<PageRef | undefined>((resolve, reject) => {
            addPage(container, { ...widgets, ...builderComponents })
              .then((pageRef) => {
                if (pageRef) {
                  addedToStage.pages[guid] = pageRef;
                }
                resolve(pageRef);
              })
              .catch((error: any) => {
                return reject(
                  createUnifiedComponentsInstallationError(
                    ErrorType.addingUnifiedComponentFailed,
                    `Failed to add '${container.data.base?.name}' ${container.type} component`,
                  )
                    .withAppDefIds(appData.appDefinitionId)
                    .withComponentGUID(guid, errorTypeMapping[container.type])
                    .withParentError(error),
                );
              });
          }),
      );

    return chainPromisesAsync(Object.entries(pages), addSinglePage);
  }

  function checkIfShouldSkipWidget(widget: UnifiedWidget) {
    if (!context.intentInstallServices.shouldUseWidgetAppsConfiguration()) {
      return false;
    }

    const widgetPages = context.intentInstallServices.getWidgetsConfiguration();

    const widgetHasPageSelected = !!widgetPages[widget.widgetId];

    return !widgetHasPageSelected;
  }

  function getPageRefForWidget(widget: UnifiedWidget) {
    if (!context.intentInstallServices.shouldUseWidgetAppsConfiguration()) {
      return homePage;
    }

    const pagesData = context.documentServices.pages.getPagesData();

    const widgetPages = context.intentInstallServices.getWidgetsConfiguration();

    const widgetPage = pagesData.find(
      (page) => page.tpaPageId === widgetPages[widget.widgetId],
    )?.id;

    return widgetPage && widgetPage !== HOME_PAGE_ID
      ? (context.documentServices.pages.getReference(widgetPage) as PageRef)
      : homePage;
  }

  function addSingleWidget(widget: UnifiedWidget) {
    if (checkIfShouldSkipWidget(widget)) {
      return Promise.resolve();
    }

    const pageRef = getPageRefForWidget(widget);

    return biReporter.withBI(
      'NEW_COMPONENTS_INSTALL',
      {
        componentId: widget.widgetId,
        componentType: 'widget',
        appDefinitionId: appData.appDefinitionId,
      },
      () =>
        new Promise<PageRef | void>((resolve, reject) =>
          addWidget(pageRef, widget)
            .then((addedWidget) => {
              if (addedWidget) {
                addedToStage.widgets[widget.widgetId] = addedWidget.widgetRef;
              }
              resolve(pageRef);
            })
            .catch((error) =>
              reject(
                createUnifiedComponentsInstallationError(
                  ErrorType.addingUnifiedComponentFailed,
                  `Failed to add '${widget?.base?.name}' widget component`,
                )
                  .withAppDefIds(appData.appDefinitionId)
                  .withComponentGUID(widget.widgetId, 'unified-widget')
                  .withParentError(error),
              ),
            ),
        ),
    );
  }

  function addSingleBuilderComponent(component: BuilderComponent) {
    const installation =
      context.componentsPlatformService.manifest.getInstallationByComponentType(
        component.data.type!,
      );

    const pageRef =
      (context.documentServices.pages.getCurrentPage() as PageRef) || homePage;

    return biReporter.withBI(
      'NEW_COMPONENTS_INSTALL',
      {
        componentId: component.componentId,
        componentType: 'widget',
        appDefinitionId: appData.appDefinitionId,
      },
      () =>
        new Promise<CompRef | EditorPointer | void>((resolve, reject) =>
          addComponent(pageRef, component)
            .then((widgetRef: CompRef | undefined) => {
              if (widgetRef) {
                addedToStage.widgets[component.componentId] = widgetRef;
              }
              resolve(widgetRef);
            })
            .catch((error) =>
              reject(
                createUnifiedComponentsInstallationError(
                  ErrorType.addingUnifiedComponentFailed,
                  `Failed to add '${component.data.type}' builder component`,
                )
                  .withAppDefIds(appData.appDefinitionId)
                  .withComponentGUID(component.componentId, 'builder-component')
                  .withStaticContainer(installation?.staticContainer)
                  .withParentError(error),
              ),
            ),
        ),
    );
  }

  function installUnifiedWidgets() {
    return new Promise<void>((resolve, reject) => {
      const widgetsArray = Object.values(widgets);
      if (widgetsArray.length === 0) {
        return resolve();
      }

      chainPromisesAsync(widgetsArray, addSingleWidget)
        .then(resolve)
        .catch(reject);
    });
  }

  function installBuilderComponents() {
    return new Promise<void>((resolve, reject) => {
      const componentsArray = Object.values(builderComponents);
      if (componentsArray.length === 0) {
        return resolve();
      }

      chainPromisesAsync(componentsArray, addSingleBuilderComponent)
        .then(resolve)
        .catch(reject);
    });
  }

  function addSingleRouter(
    router: RouterWithRequiredData,
    pagesToConnect: { pageRef: PageRef; pageRole: string }[],
  ) {
    return new Promise<RouterRef | void>((resolve, reject) => {
      try {
        // TODO: [EP-8488] Consolidate with addRouterExtension in @wix/editor-platform-private-api
        const prefix = ensureValidRouterPrefix({
          appDefinitionId: appData.appDefinitionId,
          documentServices: context.documentServices,
          router,
        });
        addRouter({
          appDefinitionId: appData.appDefinitionId,
          extensionId: router.extensionId,
          pagesToConnect,
          prefix,
        })
          .then(resolve)
          .catch(reject);
      } catch (error) {
        reject(error);
      }
    });
  }

  function installRouters(addedPages: Record<string, PageRef>) {
    return new Promise<void>((resolve, reject) => {
      const allRouters = Object.values(routers);
      const routersArray = allRouters.filter(
        (router): router is RouterWithRequiredData =>
          !!router?.data?.preferredPrefix &&
          Array.isArray(router?.data?.pages) &&
          router.data.pages.length > 0,
      );
      if (routersArray.length === 0) {
        // Routers exist in the collection but all were silently dropped because
        // their data is missing preferredPrefix or has an empty pages array —
        // likely a component catalog misconfiguration.
        if (allRouters.length > 0) {
          biReporter.reportBI('PLATFORM_ERROR', {
            errorType: 'routerFilteredOut',
            errorCode: 'ALL_ROUTERS_FILTERED',
            errorMessage:
              'All routers were filtered from installation due to missing prefix or empty pages',
            errorData: '',
            errorDetails: appData.appDefinitionId,
          });
        }
        return resolve();
      }
      chainPromisesAsync(routersArray, (router) => {
        const pagesToConnect = router.data.pages
          .filter(
            (page) => page.pageExtentionId && addedPages[page.pageExtentionId],
          )
          .map((page) => ({
            pageRef: addedPages[page.pageExtentionId!],
            pageRole: page.pageRole ?? '',
          }));
        return biReporter.withBI(
          'NEW_COMPONENTS_INSTALL',
          {
            componentId: router.extensionId,
            componentType: 'router',
            appDefinitionId: appData.appDefinitionId,
            number_installed_components: pagesToConnect.length, // connected pages installed with this router
          },
          () => addSingleRouter(router, pagesToConnect),
        );
      })
        .then(resolve)
        .catch(reject);
    });
  }

  return installUnifiedPages()
    .then(installUnifiedWidgets)
    .then(installBuilderComponents)
    .then(() => installRouters(addedToStage.pages))
    .then(() => addedToStage);
}
